import { Add, Delete, Downloading, Edit, FileDownload, Payment, Print, Search, ViewColumn } from "@mui/icons-material";
import {
    Alert,
    Box,
    Breadcrumbs,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Grid,
    IconButton,
    MenuItem,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography, useMediaQuery, useTheme
} from '@mui/material';
import { ClearIcon } from "@mui/x-date-pickers";
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Swal from "sweetalert2";
import api from '../../api';
import { useListFetch } from '../../hooks/useListFetch';
import DidNotLoad from "../../components/DidNotLoad";
import Loading from "../../components/Loading";
import { generatePDF } from "../../components/order/export_pdf";
import PaymentModal from "../../components/order/PaymentModal";
import PaymentReceiptModal from "../../components/order/PaymentReceiptModal";
import OrderDetailsModal from "../../components/order/OrderDetailsModal";
import ExternalOrderIdDisplay from "../../components/order/ExternalOrderIdDisplay";
import CustomPagination from "../../components/Pagination";
import ActionBubbleTooltip from '../../components/ActionBubbleTooltip';
import CustomerDisplayName from '../../components/CustomerDisplayName';
import { DEFAULT_LIST_PAGE_SIZE, LIST_PAGE_SIZE_OPTIONS } from '../../constants/listPagination';
import { isCaptureModeEnabled } from '../../helpers/captureMode';
import { orderListChannelTint } from '../../helpers/salesChannels';
import { formatCurrencyInCents, formatDate, formatUuid, presentUuid } from '../../helpers/formatters';
import {
    buildOrdersListLocation,
    buildOrdersListQuery,
    buildOrdersSummaryQuery,
    defaultOrdersDateRange,
    EMPTY_ORDER_SUMMARY,
    formatOrdersPeriodLabel,
    normalizeOrdersDateRange,
    orderEditPath,
    ordersDateRangeForApi,
    parseOrdersListSearch,
} from '../../helpers/orders';
import {
    downloadOrdersSalesReportCsv,
    fetchAllOrdersForReport,
} from '../../helpers/ordersSalesReportCsv';
import SalesChannelSelect from '../../components/checkout/SalesChannelSelect';
import OrderStageChips from '../../components/order/OrderStageChips';
import QuotaLimitedButton from '../../components/subscription/QuotaLimitedButton';
import PageTutorialButtons from '../../components/tutorials/PageTutorialButtons';
import {
    LOGISTICS_STATUSES,
    PAYOUT_STATUSES,
    PRODUCTION_STATUSES,
    logisticsLabel,
    payoutLabel,
    productionLabel,
} from '../../helpers/orderStages';

function OrdersPage() {
    const [orders, setOrders] = useState([]);
    const [isLoading, setIsLoading] = useState(true); // Estado para controlar o carregamento
    const [listReady, setListReady] = useState(false);
    const listReadyRef = useRef(false);
    const navigate = useNavigate(); // Usando useNavigate para redirecionamento
    const location = useLocation(); // Para acessar parâmetros da URL
    const [didNotLoad, setDidNotLoad] = useState(false);
    const [currentPage, setCurrentPage] = useState(1); // Página atual
    const [totalPages, setTotalPages] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_LIST_PAGE_SIZE);
    const [urlBoot] = useState(() => parseOrdersListSearch(location.search));
    const [searchTerm, setSearchTerm] = useState(urlBoot.searchTerm);
    const [appliedSearchTerm, setAppliedSearchTerm] = useState(Boolean(urlBoot.searchTerm));
    const [activeSearchQuery, setActiveSearchQuery] = useState(urlBoot.searchTerm);
    const [urlSearchTerm, setUrlSearchTerm] = useState(urlBoot.searchTerm);
    const [loadingDelete, setLoadingDelete] = useState(false);
    const [paymentReceiptModal, setPaymentReceiptModal] = useState({ open: false, order: null });
    const [detailsModal, setDetailsModal] = useState({ open: false, orderId: null });
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const hideProductNames = isCaptureModeEnabled();
    const [loadingPDF, setLoadingPDF] = useState(false);
    const defaultDateRange = useMemo(() => defaultOrdersDateRange(), []);
    const [draftDateFrom, setDraftDateFrom] = useState(urlBoot.from);
    const [draftDateTo, setDraftDateTo] = useState(urlBoot.to);
    const [draftSalesChannelId, setDraftSalesChannelId] = useState(urlBoot.salesChannelId);
    const [draftSalesChannelName, setDraftSalesChannelName] = useState(urlBoot.salesChannelName);
    const [filters, setFilters] = useState({
        from: urlBoot.from,
        to: urlBoot.to,
        salesChannelId: urlBoot.salesChannelId,
        salesChannelName: urlBoot.salesChannelName,
        customerUuid: urlBoot.customerUuid,
        customerName: urlBoot.customerName,
        productionStatus: urlBoot.productionStatus,
        logisticsStatus: urlBoot.logisticsStatus,
        payoutStatus: urlBoot.payoutStatus,
        paymentStatus: urlBoot.paymentStatus,
    });
    const [draftProductionStatus, setDraftProductionStatus] = useState(urlBoot.productionStatus);
    const [draftLogisticsStatus, setDraftLogisticsStatus] = useState(urlBoot.logisticsStatus);
    const [draftPayoutStatus, setDraftPayoutStatus] = useState(urlBoot.payoutStatus);
    const [draftPaymentStatus, setDraftPaymentStatus] = useState(urlBoot.paymentStatus);
    const [summary, setSummary] = useState(EMPTY_ORDER_SUMMARY);
    const [summaryLoading, setSummaryLoading] = useState(false);
    const [exportingCsv, setExportingCsv] = useState(false);

    const handlePrintReceipt = (order, storeName = "") => {
        setLoadingPDF(true);
        generatePDF(order);
        setTimeout(() => {
            setLoadingPDF(false);
        }, 1000)
    };

    const handleCreateCheckout = () => {
        navigate('/admin/orders/new');
    };

    const listReturnPath = useMemo(() => buildOrdersListLocation({
        q: activeSearchQuery,
        from: filters.from,
        to: filters.to,
        salesChannelId: filters.salesChannelId,
        salesChannelName: filters.salesChannelName,
        customerUuid: filters.customerUuid,
        customerName: filters.customerName,
        productionStatus: filters.productionStatus,
        logisticsStatus: filters.logisticsStatus,
        payoutStatus: filters.payoutStatus,
        paymentStatus: filters.paymentStatus,
    }), [
        activeSearchQuery,
        filters.from,
        filters.to,
        filters.salesChannelId,
        filters.salesChannelName,
        filters.customerUuid,
        filters.customerName,
        filters.productionStatus,
        filters.logisticsStatus,
        filters.payoutStatus,
        filters.paymentStatus,
    ]);

    // A volta da edição chega com os filtros na URL. Sem isso, o estado da lista se perde.
    useEffect(() => {
        const urlParams = new URLSearchParams(location.search);
        const hasListParams = [
            'search',
            'from',
            'to',
            'sales_channel_id',
            'customer_uuid',
            'production_status',
            'logistics_status',
            'payout_status',
            'payment_status',
        ].some((key) => urlParams.has(key));
        if (!hasListParams) {
            return;
        }
        const parsed = parseOrdersListSearch(location.search);
        setSearchTerm(parsed.searchTerm);
        setActiveSearchQuery(parsed.searchTerm);
        setUrlSearchTerm(parsed.searchTerm);
        setAppliedSearchTerm(Boolean(parsed.searchTerm));
        setDraftDateFrom(parsed.from);
        setDraftDateTo(parsed.to);
        setDraftSalesChannelId(parsed.salesChannelId);
        setDraftSalesChannelName(parsed.salesChannelName);
        setDraftProductionStatus(parsed.productionStatus);
        setDraftLogisticsStatus(parsed.logisticsStatus);
        setDraftPayoutStatus(parsed.payoutStatus);
        setDraftPaymentStatus(parsed.paymentStatus);
        setFilters({
            from: parsed.from,
            to: parsed.to,
            salesChannelId: parsed.salesChannelId,
            salesChannelName: parsed.salesChannelName,
            customerUuid: parsed.customerUuid,
            customerName: parsed.customerName,
            productionStatus: parsed.productionStatus,
            logisticsStatus: parsed.logisticsStatus,
            payoutStatus: parsed.payoutStatus,
            paymentStatus: parsed.paymentStatus,
        });
        setCurrentPage(1);
    }, [location.search]);

    const fetchSummary = useCallback(() => {
        setSummaryLoading(true);
        const summaryQuery = buildOrdersSummaryQuery({
            q: activeSearchQuery,
            from: filters.from,
            to: filters.to,
            salesChannelId: filters.salesChannelId,
            customerUuid: filters.customerUuid,
        });
        return api.get(`orders/summary?${summaryQuery}`)
            .then((response) => {
                setSummary(response.data || EMPTY_ORDER_SUMMARY);
            })
            .catch(() => {
                setSummary(EMPTY_ORDER_SUMMARY);
            })
            .finally(() => {
                setSummaryLoading(false);
            });
    }, [activeSearchQuery, filters.from, filters.to, filters.salesChannelId, filters.customerUuid]);

    const fetchOrders = useCallback((options = {}) => {
        const silent = options?.silent === true;
        if (!silent && !listReadyRef.current) {
            setIsLoading(true);
        }
        const listQuery = buildOrdersListQuery({
            page: currentPage,
            limit: itemsPerPage,
            q: activeSearchQuery,
            from: filters.from,
            to: filters.to,
            salesChannelId: filters.salesChannelId,
            customerUuid: filters.customerUuid,
            productionStatus: filters.productionStatus,
            logisticsStatus: filters.logisticsStatus,
            payoutStatus: filters.payoutStatus,
            paymentStatus: filters.paymentStatus,
        });
        api.get(`orders?${listQuery}`)
            .then(response => {
                setOrders(response.data.orders);
                setTotalPages(response.data.total_pages);
                setDidNotLoad(false);
            })
            .catch(() => {
                setDidNotLoad(true);
            })
            .finally(() => {
                listReadyRef.current = true;
                setListReady(true);
                setIsLoading(false);
            });
    }, [currentPage, itemsPerPage, activeSearchQuery, filters.from, filters.to, filters.salesChannelId, filters.customerUuid, filters.productionStatus, filters.logisticsStatus, filters.payoutStatus, filters.paymentStatus]);

    useListFetch(fetchOrders, [currentPage, itemsPerPage, activeSearchQuery, filters.from, filters.to, filters.salesChannelId, filters.customerUuid, filters.productionStatus, filters.logisticsStatus, filters.payoutStatus, filters.paymentStatus]);
    useListFetch(fetchSummary, [activeSearchQuery, filters.from, filters.to, filters.salesChannelId, filters.customerUuid]);

    const handleSearch = () => {
        setCurrentPage(1);
        setActiveSearchQuery(searchTerm.trim());
        setAppliedSearchTerm(Boolean(searchTerm.trim()));
        setItemsPerPage(DEFAULT_LIST_PAGE_SIZE);
    };

    const handleClearSearch = () => {
        setSearchTerm('');
        setActiveSearchQuery('');
        setCurrentPage(1);
        setAppliedSearchTerm(false);
        setItemsPerPage(DEFAULT_LIST_PAGE_SIZE);
    };

    const handleDraftDateFromChange = (value) => {
        setDraftDateFrom(value);
        if (!value) {
            return;
        }
        const normalized = normalizeOrdersDateRange(value, draftDateTo || value);
        setDraftDateFrom(normalized.from);
        setDraftDateTo(normalized.to);
    };

    const handleDraftDateToChange = (value) => {
        setDraftDateTo(value);
        if (!value) {
            return;
        }
        const normalized = normalizeOrdersDateRange(draftDateFrom || value, value);
        setDraftDateFrom(normalized.from);
        setDraftDateTo(normalized.to);
    };

    const handleApplyFilters = () => {
        const range = ordersDateRangeForApi(draftDateFrom, draftDateTo);
        if (range.error) {
            Swal.fire('Período inválido', range.error, 'warning');
            return;
        }
        const normalized = normalizeOrdersDateRange(range.from, range.to);
        setDraftDateFrom(normalized.from);
        setDraftDateTo(normalized.to);
        setFilters((prev) => ({
            from: normalized.from,
            to: normalized.to,
            salesChannelId: draftSalesChannelId || '',
            salesChannelName: draftSalesChannelId ? draftSalesChannelName : '',
            customerUuid: prev.customerUuid || '',
            customerName: prev.customerName || '',
            productionStatus: draftProductionStatus || '',
            logisticsStatus: draftLogisticsStatus || '',
            payoutStatus: draftPayoutStatus || '',
            paymentStatus: draftPaymentStatus || '',
        }));
        setCurrentPage(1);
    };

    const handleClearFilters = () => {
        setDraftDateFrom('');
        setDraftDateTo('');
        setDraftSalesChannelId('');
        setDraftSalesChannelName('');
        setDraftProductionStatus('');
        setDraftLogisticsStatus('');
        setDraftPayoutStatus('');
        setDraftPaymentStatus('');
        setFilters({
            from: '',
            to: '',
            salesChannelId: '',
            salesChannelName: '',
            customerUuid: '',
            customerName: '',
            productionStatus: '',
            logisticsStatus: '',
            payoutStatus: '',
            paymentStatus: '',
        });
        setCurrentPage(1);
        if (location.search) {
            navigate('/admin/orders/list', { replace: true });
        }
    };

    const handleResetFilters = () => {
        const next = defaultOrdersDateRange();
        setDraftDateFrom(next.from);
        setDraftDateTo(next.to);
        setDraftSalesChannelId('');
        setDraftSalesChannelName('');
        setDraftProductionStatus('');
        setDraftLogisticsStatus('');
        setDraftPayoutStatus('');
        setDraftPaymentStatus('');
        setFilters({
            from: next.from,
            to: next.to,
            salesChannelId: '',
            salesChannelName: '',
            customerUuid: '',
            customerName: '',
            productionStatus: '',
            logisticsStatus: '',
            payoutStatus: '',
            paymentStatus: '',
        });
        setCurrentPage(1);
        if (location.search) {
            navigate('/admin/orders/list', { replace: true });
        }
    };

    const handleClearCustomerFilter = () => {
        setFilters((prev) => ({
            ...prev,
            customerUuid: '',
            customerName: '',
        }));
        setCurrentPage(1);
        const params = new URLSearchParams(location.search);
        params.delete('customer_uuid');
        params.delete('customer_name');
        const next = params.toString();
        navigate(next ? `/admin/orders/list?${next}` : '/admin/orders/list', { replace: true });
    };

    const handleExportCsv = async () => {
        if (exportingCsv) {
            return;
        }
        setExportingCsv(true);
        try {
            const allOrders = await fetchAllOrdersForReport(api, {
                q: activeSearchQuery,
                from: filters.from,
                to: filters.to,
                salesChannelId: filters.salesChannelId,
                customerUuid: filters.customerUuid,
            });
            if (!allOrders.length) {
                await Swal.fire({
                    icon: 'info',
                    title: 'Nenhuma venda',
                    text: 'Não há vendas no período/filtros atuais para exportar.',
                });
                return;
            }
            const stamp = `${filters.from || 'inicio'}_${filters.to || 'fim'}`;
            downloadOrdersSalesReportCsv(allOrders, `relatorio-vendas-${stamp}.csv`);
        } catch (error) {
            await Swal.fire({
                icon: 'error',
                title: 'Falha ao exportar',
                text: 'Não foi possível gerar o relatório CSV. Tente novamente.',
            });
        } finally {
            setExportingCsv(false);
        }
    };

    const periodLabel = formatOrdersPeriodLabel(filters.from, filters.to);
    const monthEndStr = defaultDateRange.to;

    const handleDeleteOrder = (orderId) => {
        Swal.fire({
            title: 'Você tem certeza?',
            text: 'Esta venda será removida permanentemente!',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: loadingDelete ? '<i class="fas fa-spinner fa-spin"></i> Removendo...' : 'Sim, remover!',
            cancelButtonText: 'Cancelar',
            allowOutsideClick: !loadingDelete, // Impede que o SweetAlert seja fechado durante o carregamento
            preConfirm: () => {
                if (!loadingDelete) {
                    setLoadingDelete(true);
                    return api.delete(`orders/${orderId}`)
                        .then(response => {
                            setLoadingDelete(false);
                            // Atualiza o estado após remoção
                            setOrders(orders.filter(order => order.id !== orderId));
                            fetchSummary();
                            Swal.fire('Removido!', 'Venda removida com sucesso.', 'success');
                        })
                        .catch(error => {
                            setLoadingDelete(false);
                            console.error('Erro ao remover a venda:', error);
                            Swal.fire('Erro!', 'Houve um problema ao remover a venda.', 'error');
                        });
                }
            }
        });
    };

    const handleRegisterPayment = (orderId) => {
        const order = orders.find(o => o.id === orderId);
        if (!order?.payment_details?.paid) {
            return;
        }
        if (order) {
            setPaymentReceiptModal({ open: true, order });
        }
    };

    const handleClosePaymentReceiptModal = (success) => {
        setPaymentReceiptModal({ open: false, order: null });

        if (success) {
            fetchOrders();
            fetchSummary();
        }
    };

    const handleOpenDetails = (orderId) => {
        setDetailsModal({ open: true, orderId });
    };

    const handleEditOrder = (orderId) => {
        navigate(orderEditPath(orderId, listReturnPath));
    };

    const handleCloseDetails = () => {
        setDetailsModal({ open: false, orderId: null });
        fetchOrders();
    };

    if (isLoading && !listReady) {
        return <Loading />;
    }

    if (didNotLoad) {
        return <DidNotLoad />
    }

    return (
        <div>
            <Breadcrumbs aria-label="breadcrumb">
                <Link color="inherit" onClick={() => navigate('/admin')} to={'/admin'}>
                    Dashboard
                </Link>
                <Typography color="text.primary">Pedidos</Typography>
                <Typography color="text.primary">Lista</Typography>
            </Breadcrumbs>
            <br />

            <Box sx={{
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                justifyContent: 'space-between',
                alignItems: isMobile ? 'stretch' : 'center',
                marginBottom: 2,
                gap: isMobile ? 2 : 0
            }}>
                {/* Título */}
                <Typography variant="h4" gutterBottom>
                    Pedidos
                </Typography>

                {/* Campo de busca + botões */}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: isMobile ? 'column' : 'row',
                        alignItems: 'center',
                        gap: 2,
                        width: isMobile ? '100%' : 'auto'
                    }}
                >
                    <TextField
                        variant="outlined"
                        size="small"
                        placeholder="Buscar por cliente"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        fullWidth={isMobile}
                    />
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSearch}
                        startIcon={<Search />}
                        fullWidth={isMobile}
                    >
                        Buscar
                    </Button>
                    {/* Botão de venda */}
                    <Box sx={{ display: 'flex', justifyContent: isMobile ? 'center' : 'flex-end', alignItems: 'center', gap: 1 }}>
                        <PageTutorialButtons pageKey="orders" fullWidth={isMobile} />
                        <Button
                            variant="outlined"
                            onClick={() => navigate('/admin/orders')}
                            startIcon={<ViewColumn />}
                            fullWidth={isMobile}
                        >
                            Quadro de pedidos
                        </Button>
                        <QuotaLimitedButton
                            quotaKey="checkouts"
                            variant="contained"
                            color="primary"
                            onClick={handleCreateCheckout}
                            startIcon={<Add />}
                            fullWidth={isMobile}
                        >
                            Pedido
                        </QuotaLimitedButton>
                    </Box>
                    {appliedSearchTerm && (
                        <Button
                            variant="outlined"
                            color="secondary"
                            onClick={handleClearSearch}
                            startIcon={<ClearIcon />}
                            fullWidth={isMobile}
                        >
                            Limpar Busca
                        </Button>
                    )}
                </Box>
            </Box>

            <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle1" sx={{ mb: 1.5, fontWeight: 600 }}>
                    Filtros dos pedidos
                </Typography>
                <Box
                    sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 2,
                        alignItems: 'center',
                        mb: 1.5,
                    }}
                >
                    <TextField
                        label="Data inicial"
                        type="date"
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        value={draftDateFrom}
                        onChange={(e) => handleDraftDateFromChange(e.target.value)}
                        inputProps={{ max: draftDateTo || monthEndStr }}
                        sx={{ minWidth: 160, flex: '1 1 160px' }}
                    />
                    <TextField
                        label="Data final"
                        type="date"
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        value={draftDateTo}
                        onChange={(e) => handleDraftDateToChange(e.target.value)}
                        inputProps={{
                            min: draftDateFrom || undefined,
                            max: monthEndStr,
                        }}
                        disabled={!draftDateFrom}
                        sx={{ minWidth: 160, flex: '1 1 160px' }}
                    />
                    <Box sx={{ minWidth: 200, flex: '1 1 220px' }}>
                        <SalesChannelSelect
                            label="Canal de vendas"
                            size="small"
                            value={draftSalesChannelId}
                            allowEmpty
                            emptyLabel="Todos os canais"
                            onChange={(e, channel) => {
                                const nextId = e.target.value;
                                setDraftSalesChannelId(nextId);
                                setDraftSalesChannelName(nextId ? (channel?.name || '') : '');
                            }}
                        />
                    </Box>
                    <TextField
                        select
                        label="Produção"
                        size="small"
                        value={draftProductionStatus}
                        onChange={(e) => setDraftProductionStatus(e.target.value)}
                        sx={{ minWidth: 160, flex: '1 1 160px' }}
                    >
                        <MenuItem value="">Todas</MenuItem>
                        {PRODUCTION_STATUSES.map((status) => (
                            <MenuItem key={status} value={status}>{productionLabel(status)}</MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        select
                        label="Logística"
                        size="small"
                        value={draftLogisticsStatus}
                        onChange={(e) => setDraftLogisticsStatus(e.target.value)}
                        sx={{ minWidth: 180, flex: '1 1 180px' }}
                    >
                        <MenuItem value="">Todas</MenuItem>
                        {LOGISTICS_STATUSES.map((status) => (
                            <MenuItem key={status} value={status}>{logisticsLabel(status)}</MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        select
                        label="Repasse"
                        size="small"
                        value={draftPayoutStatus}
                        onChange={(e) => setDraftPayoutStatus(e.target.value)}
                        sx={{ minWidth: 150, flex: '1 1 150px' }}
                    >
                        <MenuItem value="">Todos</MenuItem>
                        {PAYOUT_STATUSES.map((status) => (
                            <MenuItem key={status} value={status}>{payoutLabel(status)}</MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        select
                        label="Pagamento do cliente"
                        size="small"
                        value={draftPaymentStatus}
                        onChange={(e) => setDraftPaymentStatus(e.target.value)}
                        sx={{ minWidth: 180, flex: '1 1 180px' }}
                    >
                        <MenuItem value="">Todos</MenuItem>
                        <MenuItem value="paid">Pago</MenuItem>
                        <MenuItem value="pending">Pendente</MenuItem>
                    </TextField>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                        <Button variant="contained" onClick={handleApplyFilters}>
                            Filtrar
                        </Button>
                        <Button
                            variant="outlined"
                            color="secondary"
                            onClick={handleClearFilters}
                            startIcon={<ClearIcon />}
                        >
                            Limpar filtro
                        </Button>
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                    <Button variant="outlined" size="small" onClick={handleResetFilters}>
                        Mês atual
                    </Button>
                    <Button
                        variant="outlined"
                        size="small"
                        color="secondary"
                        onClick={handleExportCsv}
                        disabled={exportingCsv}
                        startIcon={exportingCsv ? <CircularProgress size={16} /> : <FileDownload />}
                    >
                        {exportingCsv ? 'Exportando…' : 'Exportar CSV'}
                    </Button>
                    {periodLabel && (
                        <Chip
                            size="small"
                            color="primary"
                            variant="outlined"
                            label={periodLabel}
                        />
                    )}
                    {!periodLabel && filters.customerUuid && (
                        <Chip
                            size="small"
                            color="primary"
                            variant="outlined"
                            label="Sem filtro de data"
                        />
                    )}
                    {filters.salesChannelName && (
                        <Chip
                            size="small"
                            color="secondary"
                            variant="outlined"
                            label={filters.salesChannelName}
                        />
                    )}
                    {filters.productionStatus && (
                        <Chip size="small" variant="outlined" label={`Produção: ${productionLabel(filters.productionStatus)}`} />
                    )}
                    {filters.logisticsStatus && (
                        <Chip size="small" variant="outlined" label={`Logística: ${logisticsLabel(filters.logisticsStatus)}`} />
                    )}
                    {filters.payoutStatus && (
                        <Chip size="small" variant="outlined" label={`Repasse: ${payoutLabel(filters.payoutStatus)}`} />
                    )}
                    {filters.paymentStatus && (
                        <Chip
                            size="small"
                            variant="outlined"
                            label={filters.paymentStatus === 'paid' ? 'Cliente: pago' : 'Cliente: pendente'}
                        />
                    )}
                    {filters.customerUuid && (
                        <Chip
                            size="small"
                            color="info"
                            variant="outlined"
                            label={filters.customerName || 'Cliente filtrado'}
                            onDelete={handleClearCustomerFilter}
                        />
                    )}
                </Box>
            </Paper>

            <Grid container spacing={2} sx={{ mb: 2 }} alignItems="stretch">
                <Grid item xs={12} sm={6} md={3}>
                    <Card variant="outlined" sx={{ height: '100%' }}>
                        <CardContent sx={{ display: 'flex', gap: 2, height: '100%' }}>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography variant="body2" color="text.secondary">
                                    Total de vendas
                                </Typography>
                                <Typography variant="h4">
                                    {summaryLoading ? <CircularProgress size={28} /> : (summary.total_sales ?? 0)}
                                </Typography>
                            </Box>
                            <Box sx={{ width: '1px', alignSelf: 'stretch', bgcolor: 'divider' }} />
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography variant="body2" color="text.secondary">
                                    Total de produtos
                                </Typography>
                                <Typography variant="h4">
                                    {summaryLoading ? <CircularProgress size={28} /> : (summary.total_products ?? 0)}
                                </Typography>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card variant="outlined" sx={{ height: '100%' }}>
                        <CardContent>
                            <Typography variant="body2" color="text.secondary">
                                Total de faturamento
                            </Typography>
                            <Typography variant="h4">
                                {summaryLoading
                                    ? <CircularProgress size={28} />
                                    : formatCurrencyInCents(summary.total_amount ?? 0)}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card variant="outlined" sx={{ height: '100%' }}>
                        <CardContent>
                            <Typography variant="body2" color="text.secondary">
                                Ticket médio
                            </Typography>
                            <Typography variant="h4">
                                {summaryLoading
                                    ? <CircularProgress size={28} />
                                    : formatCurrencyInCents(summary.average_ticket ?? 0)}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card variant="outlined" sx={{ height: '100%' }}>
                        <CardContent>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                Top 5 produtos
                            </Typography>
                            {summaryLoading ? (
                                <CircularProgress size={28} />
                            ) : (summary.top_products || []).length > 0 ? (
                                (summary.top_products || []).map((product, index) => (
                                    <Typography
                                        key={`${product.name}-${index}`}
                                        variant="body2"
                                        sx={{ lineHeight: 1.4 }}
                                    >
                                        {index + 1}. {hideProductNames ? '******' : product.name}: {product.quantity} un.
                                    </Typography>
                                ))
                            ) : (
                                <Typography variant="body2" color="text.secondary">
                                    Nenhum produto no período
                                </Typography>
                            )}
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Alerta de destaque quando há busca por URL */}
            {urlSearchTerm && (
                <Alert
                    severity="info"
                    sx={{ mb: 2 }}
                    action={
                        <Chip
                            label={`Pedido: ${urlSearchTerm}`}
                            color="warning"
                            size="small"
                        />
                    }
                >
                    Destaque aplicado ao pedido pesquisado. O pedido correspondente será destacado na lista abaixo.
                </Alert>
            )}

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold' }}>ID</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Cliente</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Etapas</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Produtos</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Pagamento</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Data da venda</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Ações</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {Array.isArray(orders) && orders.length > 0 && orders.map((order) => {
                            const isHighlighted = urlSearchTerm && formatUuid(order.id) === urlSearchTerm;
                            const channelTint = orderListChannelTint(order.sales_channel?.name);
                            const channelBackground = (theme, hover = false) => {
                                const dark = theme.palette.mode === 'dark';
                                if (channelTint === 'shopee') {
                                    if (dark) {
                                        return hover ? 'rgba(255, 153, 64, 0.28)' : 'rgba(255, 153, 64, 0.16)';
                                    }
                                    return hover ? '#FFE8D4' : '#FFF4EA';
                                }
                                if (channelTint === 'mercado_livre') {
                                    if (dark) {
                                        return hover ? 'rgba(255, 214, 0, 0.30)' : 'rgba(255, 214, 0, 0.18)';
                                    }
                                    return hover ? '#FFF3B0' : '#FFF8D6';
                                }
                                return null;
                            };
                            return (
                                <TableRow
                                    key={order.id}
                                    sx={(theme) => ({
                                        backgroundColor: isHighlighted
                                            ? theme.palette.warning.light + '40'
                                            : channelBackground(theme)
                                                || (!order.payment_details.paid
                                                    ? theme.palette.mode === 'dark'
                                                        ? theme.palette.action.hover
                                                        : theme.palette.action.selected
                                                    : 'inherit'),
                                        border: isHighlighted
                                            ? `2px solid ${theme.palette.warning.main}`
                                            : 'none',
                                        '& > .MuiTableCell-root': {
                                            backgroundColor: 'inherit',
                                        },
                                        '&:hover': {
                                            backgroundColor: isHighlighted
                                                ? theme.palette.warning.light + '60'
                                                : channelBackground(theme, true) || theme.palette.action.hover,
                                        },
                                        '&:hover > .MuiTableCell-root': {
                                            backgroundColor: 'inherit',
                                        },
                                        transition: 'all 0.3s ease', // Animação suave para todas as propriedades
                                        transform: isHighlighted ? 'scale(1.01)' : 'scale(1)', // Leve aumento no tamanho
                                        boxShadow: isHighlighted
                                            ? `0 4px 8px ${theme.palette.warning.main}40`
                                            : 'none',
                                    })}
                                >
                                    <TableCell>
                                        <Typography
                                            component="button"
                                            type="button"
                                            onClick={() => handleOpenDetails(order.id)}
                                            sx={{
                                                color: theme.palette.secondary.main,
                                                fontWeight: 'bold',
                                                cursor: 'pointer',
                                                border: 'none',
                                                background: 'none',
                                                padding: 0,
                                                font: 'inherit',
                                                textAlign: 'left',
                                                '&:hover': { textDecoration: 'underline' },
                                            }}
                                        >
                                            {presentUuid(order.id)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <CustomerDisplayName customer={order.customer} />
                                        <br />
                                        Canal: {order.sales_channel?.name}
                                        {order.external_order_id ? (
                                            <>
                                                <br />
                                                <ExternalOrderIdDisplay
                                                    externalOrderId={order.external_order_id}
                                                    externalOrderUrl={order.external_order_url}
                                                    salesChannelName={order.sales_channel?.name}
                                                />
                                            </>
                                        ) : null}
                                    </TableCell>
                                    <TableCell>
                                        <OrderStageChips order={order} />
                                    </TableCell>
                                    <TableCell>
                                        {order.products.map((product, index) => (
                                            <div key={index}>
                                                - {product.name}: {product.quantity} x {formatCurrencyInCents(product.base_price)}
                                            </div>
                                        ))}
                                    </TableCell>
                                    <TableCell>
                                        Bruto: <strong>{formatCurrencyInCents(order.amount)}</strong>
                                        <br />
                                        Líquido esperado:{' '}
                                        <strong>
                                            {formatCurrencyInCents(
                                                order.expected_net != null ? order.expected_net : order.amount
                                            )}
                                        </strong>
                                        <br />
                                        Forma: {(order.payment_methods || []).map((m) => m.name).filter(Boolean).join(' + ') || '—'}
                                        <br />
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            Conciliação:
                                            {order.bank_reconciled ? (
                                                <Typography variant="body2" color="info.main" sx={{ fontWeight: 'bold' }}>
                                                    Bancária
                                                </Typography>
                                            ) : order.payment_received ? (
                                                <Typography variant="body2" color="success.main" sx={{ fontWeight: 'bold' }}>
                                                    Realizada
                                                </Typography>
                                            ) : (
                                                <Typography variant="body2" color="warning.main" sx={{ fontWeight: 'bold' }}>
                                                    Pendente
                                                </Typography>
                                            )}
                                        </Box>
                                    </TableCell>
                                    <TableCell>{formatDate(order.created_at)}</TableCell>
                                    <TableCell>
                                        <ActionBubbleTooltip title="Editar pedido">
                                            <IconButton
                                                color="primary"
                                                onClick={() => handleEditOrder(order.id)}
                                                aria-label="Editar pedido"
                                            >
                                                <Edit />
                                            </IconButton>
                                        </ActionBubbleTooltip>
                                        <ActionBubbleTooltip title="Emitir recibo da venda">
                                            <IconButton
                                                color="secondary"
                                                onClick={() => handlePrintReceipt(order)} // Chama a função de gerar o PDF
                                                aria-label="Comprovante"
                                                disabled={loadingPDF}
                                            >
                                                {loadingPDF ? <Downloading /> : <Print />}
                                            </IconButton>
                                        </ActionBubbleTooltip>
                                        {!order.payment_received && (
                                            <ActionBubbleTooltip
                                                placement="left"
                                                title={
                                                    order.payment_details?.paid
                                                        ? 'Registrar conciliação manual do recebimento bancário.'
                                                        : 'Para conciliar manualmente, informe primeiro o pagamento da venda (ícone de pagamento pendente). A conciliação bancária manual só fica disponível depois que a venda sair do status de pagamento pendente.'
                                                }
                                            >
                                                <span>
                                                    <IconButton
                                                        color="success"
                                                        onClick={() => handleRegisterPayment(order.id)}
                                                        aria-label="Conciliar pagamento"
                                                        disabled={!order.payment_details?.paid}
                                                    >
                                                        <Payment />
                                                    </IconButton>
                                                </span>
                                            </ActionBubbleTooltip>
                                        )}
                                        <IconButton
                                            color="error"
                                            onClick={() => handleDeleteOrder(order.id)}
                                            aria-label="remover venda"
                                        >
                                            <Delete />
                                        </IconButton>
                                        {!order.payment_details.paid &&
                                            <PaymentModal
                                                id={order.id}
                                                onSuccess={() => {
                                                    fetchOrders();
                                                    fetchSummary();
                                                }}
                                            />
                                        }
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 2, // Espaçamento entre o Select e a paginação
                    mt: 3, // Margem superior para afastar do conteúdo acima
                }}
            >
                <Select
                    value={itemsPerPage}
                    onChange={(event) => {
                        const newLimit = event.target.value;
                        setItemsPerPage(newLimit);
                        setCurrentPage(1);
                    }}
                    variant="standard"
                >
                    {LIST_PAGE_SIZE_OPTIONS.map((size) => (
                        <MenuItem key={size} value={size}>{size} por página</MenuItem>
                    ))}
                </Select>
                <CustomPagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={(page) => {
                        setCurrentPage(page);
                    }}
                />
            </Box>

            {/* Modal de Registro de Recebimento */}
            <PaymentReceiptModal
                open={paymentReceiptModal.open}
                onClose={handleClosePaymentReceiptModal}
                order={paymentReceiptModal.order}
            />
            <OrderDetailsModal
                open={detailsModal.open}
                orderId={detailsModal.orderId}
                editReturnTo={listReturnPath}
                onClose={handleCloseDetails}
            />
        </div>
    );
}

export default OrdersPage;
